import { useCallback, useEffect, useRef, useState } from 'react';
import { streamSearch } from '../services/api';

const IDLE = { status: 'idle', packages: [], error: null, startedAt: null, finishedAt: null };

// Packages arriving within this window are applied in one render.
const FLUSH_MS = 60;

/**
 * Runs a streaming search whenever `request` changes (by value) and exposes
 * `{ status, packages, error, startedAt, finishedAt, stop }`.
 * status: idle | searching | done | stopped | error.
 * `request`: null or { query, mode, match, sources, customFolders, nonce }.
 */
export const useSearchStream = (request) => {
  const [state, setState] = useState(IDLE);
  const activeRef = useRef(null);

  const key = request
    ? JSON.stringify([request.query, request.mode, request.match, request.sources, request.customFolders, request.nonce])
    : '';

  useEffect(() => {
    if (!request) {
      setState(IDLE);
      return undefined;
    }

    const controller = new AbortController();
    let buffer = [];
    let timer = null;
    const flush = () => {
      window.clearTimeout(timer);
      timer = null;
      if (!buffer.length) return;
      const batch = buffer;
      buffer = [];
      setState((prev) => ({ ...prev, packages: [...prev.packages, ...batch] }));
    };
    activeRef.current = { controller, flush };

    setState({ status: 'searching', packages: [], error: null, startedAt: Date.now(), finishedAt: null });

    streamSearch({
      query: request.query,
      mode: request.mode,
      fullText: request.match === 'full',
      sources: request.sources,
      customFolders: request.customFolders,
      signal: controller.signal,
      onPackage: (pkg) => {
        buffer.push(pkg);
        if (!timer) timer = window.setTimeout(flush, FLUSH_MS);
      },
    })
      .then(() => {
        flush();
        setState((prev) => ({ ...prev, status: 'done', finishedAt: Date.now() }));
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        flush();
        setState((prev) => ({ ...prev, status: 'error', error, finishedAt: Date.now() }));
      });

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const stop = useCallback(() => {
    const active = activeRef.current;
    if (!active) return;
    active.flush();
    active.controller.abort();
    setState((prev) => (prev.status === 'searching'
      ? { ...prev, status: 'stopped', finishedAt: Date.now() }
      : prev));
  }, []);

  return { ...state, stop };
};
